import { useState, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Star,
  X,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  Globe,
  FileText,
} from 'lucide-react';
import { api } from '@/lib/axios';
import { API_ROUTES, ROUTES } from '@/constants';
import { Skeleton } from '@/components/ui';
import { Select } from '@/components/forms';
import { useUsers } from '@/api/useUsers';
import styles from './ProviderStats.module.css';

// --- Country-city mapping (case/accent-insensitive with aliases) ---

function normalize(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

const CITY_COUNTRY_RAW: Record<string, string[]> = {
  Peru: [
    'Lima',
    'Arequipa',
    'Trujillo',
    'Chiclayo',
    'Cusco',
    'Piura',
    'Iquitos',
    'Huancayo',
    'Chimbote',
    'Pucallpa',
    'Tacna',
    'Ica',
    'Juliaca',
    'Ayacucho',
    'Cajamarca',
    'Huanuco',
    'Huánuco',
    'Tarapoto',
    'Puno',
    'Tumbes',
    'Huaraz',
    'Moyobamba',
    'Chachapoyas',
    'Puerto Maldonado',
    'Abancay',
    'Moquegua',
    'Cerro de Pasco',
    'Huancavelica',
    'Sullana',
    'Chincha',
    'Nazca',
    'Miraflores',
    'San Isidro',
    'Surco',
    'Santiago de Surco',
    'Barranco',
    'La Molina',
    'San Borja',
    'San Miguel',
    'Lince',
    'Magdalena',
    'Pueblo Libre',
    'Jesus Maria',
    'Jesús María',
    'Breña',
    'Chorrillos',
    'Ate',
    'Santa Anita',
    'Los Olivos',
    'Comas',
    'San Martin de Porres',
    'San Juan de Lurigancho',
    'Villa El Salvador',
    'Villa Maria del Triunfo',
    'Callao',
    'La Victoria',
    'Rimac',
    'Rímac',
    'Surquillo',
    'Chaclacayo',
    'Lurin',
    'Lurín',
    'Pachacamac',
    'Cieneguilla',
    'Ventanilla',
  ],
  Mexico: [
    'Ciudad de Mexico',
    'Ciudad de México',
    'CDMX',
    'Mexico City',
    'Guadalajara',
    'Monterrey',
    'Puebla',
    'Cancun',
    'Cancún',
    'Queretaro',
    'Querétaro',
    'Tijuana',
    'Leon MX',
    'León MX',
    'Merida',
    'Mérida',
    'Chihuahua',
    'San Luis Potosi',
    'San Luis Potosí',
    'Aguascalientes',
    'Hermosillo',
    'Saltillo',
    'Mexicali',
    'Culiacan',
    'Culiacán',
    'Zapopan',
    'Ecatepec',
    'Ciudad Juarez',
    'Ciudad Juárez',
    'Toluca',
    'Morelia',
    'Cuernavaca',
    'Oaxaca',
    'Veracruz',
    'Villahermosa',
    'Tuxtla Gutierrez',
    'Tuxtla Gutiérrez',
    'Acapulco',
    'Playa del Carmen',
    'San Miguel de Allende',
    'Los Cabos',
    'Puerto Vallarta',
    'Mazatlan',
    'Mazatlán',
    'Durango',
    'Tampico',
    'Pachuca',
    'Santa Catarina',
    'Polanco',
    'Roma Norte',
    'Roma Sur',
    'Condesa',
    'Coyoacan',
    'Coyoacán',
    'Tlalpan',
    'Xochimilco',
    'Iztapalapa',
    'Naucalpan',
    'Tlalnepantla',
    'Napoles',
    'Nápoles',
    'Jardin Paraiso',
    'Jardín Paraíso',
    'Del Valle',
    'Santa Fe',
    'Pedregal',
    'Insurgentes',
  ],
  Spain: [
    'Madrid',
    'Barcelona',
    'Valencia',
    'Sevilla',
    'Malaga',
    'Málaga',
    'Bilbao',
    'Zaragoza',
    'Murcia',
    'Palma',
    'Palma de Mallorca',
    'Las Palmas',
    'Las Palmas de Gran Canaria',
    'Tenerife',
    'Alicante',
    'Cordoba',
    'Córdoba',
    'Valladolid',
    'Vigo',
    'Gijon',
    'Gijón',
    'A Coruna',
    'A Coruña',
    'Granada',
    'Vitoria',
    'Vitoria-Gasteiz',
    'Oviedo',
    'Pamplona',
    'Santander',
    'San Sebastian',
    'San Sebastián',
    'Salamanca',
    'Burgos',
    'Albacete',
    'Logrono',
    'Logroño',
    'Badajoz',
    'Huelva',
    'Tarragona',
    'Lleida',
    'Girona',
    'Cadiz',
    'Cádiz',
    'Jaen',
    'Jaén',
    'Almeria',
    'Almería',
    'Toledo',
    'Leon',
    'León',
    'Castellon',
    'Castellón',
    'Navarra',
    'Marbella',
    'Ibiza',
    'Muxika',
    'Segovia',
    'Sepulveda',
    'Sepúlveda',
    'Sotos de Sepulveda',
  ],
};

const CITY_LOOKUP = new Map<string, string>();
for (const [country, cities] of Object.entries(CITY_COUNTRY_RAW)) {
  for (const city of cities) {
    CITY_LOOKUP.set(normalize(city), country);
  }
}

function cityToCountry(city: string): string {
  if (!city) return 'Other';
  // Try exact normalized match
  const direct = CITY_LOOKUP.get(normalize(city));
  if (direct) return direct;
  // Try each comma-separated part (handles "San Isidro, Lima")
  for (const part of city.split(',')) {
    const match = CITY_LOOKUP.get(normalize(part));
    if (match) return match;
  }
  return 'Other';
}

type CountryFilter = 'All' | 'Peru' | 'Mexico' | 'Spain' | 'Other';
const COUNTRY_OPTIONS: CountryFilter[] = ['All', 'Peru', 'Mexico', 'Spain', 'Other'];

const COUNTRY_COLORS: Record<string, string> = {
  Peru: '#6366f1',
  Mexico: '#10b981',
  Spain: '#f59e0b',
  Other: '#94a3b8',
};

// --- Types ---
interface ServiceCount {
  service: string;
  slug: string;
  count: number;
}
interface CityCount {
  city: string;
  count: number;
}
interface CountryServiceData {
  country: string;
  total_offerings: number;
  provider_count: number;
  services: { service: string; count: number }[];
}
interface DashboardStats {
  overview: {
    total: number;
    active: number;
    inactive: number;
    draft: number;
    with_email: number;
    with_phone: number;
    with_both_contacts: number;
    with_documents: number;
    document_distribution: Record<string, number>;
  };
  by_city: CityCount[];
  by_service: ServiceCount[];
  by_country_service: CountryServiceData[];
  state_distribution: { state: string; count: number }[];
  rating_distribution: { rating: number | string; count: number }[];
}

interface ProviderResult {
  id: string;
  nombre_comercial: string;
  ciudad: string;
  calificacion: number;
  email: string;
  telefono: string;
  state: string;
  created_by_email?: string | null;
  services: { id: string; service_details?: { name: string } }[];
}

interface ProviderListResponse {
  count: number;
  results: ProviderResult[];
}

// --- Detail panel filter types ---
type PanelFilter =
  | { type: 'city'; label: string; city: string }
  | { type: 'service'; label: string; slug: string }
  | { type: 'status'; label: string; state: string }
  | { type: 'rating'; label: string; rating: number };

// --- Sub-components ---

function StarRating({ value }: { value: number | null | undefined }) {
  const rating = Math.round(value ?? 0);
  return (
    <span className={styles.starRating}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star key={i} size={10} className={i < rating ? styles.starFilled : styles.starEmpty} />
      ))}
    </span>
  );
}

function BarRow({
  label,
  count,
  maxCount,
  color,
  onClick,
}: {
  label: string;
  count: number;
  maxCount: number;
  color: string;
  onClick: () => void;
}) {
  const pct = maxCount > 0 ? Math.max((count / maxCount) * 100, 2) : 0;
  return (
    <button className={styles.barRow} onClick={onClick} title={`${label}: ${count}`}>
      <span className={styles.barRowLabel}>{label}</span>
      <div className={styles.barTrack}>
        <div className={styles.barFill} style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className={styles.barRowCount}>{count}</span>
      <ChevronRight size={12} className={styles.barRowChevron} />
    </button>
  );
}

function DetailPanel({
  filter,
  onClose,
  creatorId,
  onProviderClick,
}: {
  filter: PanelFilter;
  onClose: () => void;
  creatorId?: string;
  onProviderClick: (id: string) => void;
}) {
  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {};
    if (filter.type === 'city') params.search = filter.city;
    if (filter.type === 'service') {
      params.state = 'activo';
      params.services__service = filter.slug;
    }
    if (filter.type === 'status' && filter.state) params.state = filter.state;
    if (filter.type === 'rating') {
      params.state = 'activo';
      params.min_rating = filter.rating;
    }
    if (creatorId) params.created_by = creatorId;
    return params;
  }, [filter, creatorId]);

  const { data, isLoading } = useQuery<ProviderListResponse>({
    queryKey: ['provider-detail-panel', filter, creatorId],
    queryFn: async () => {
      const res = await api.get('/api/providers/providers/', { params: queryParams });
      return res.data;
    },
  });

  return (
    <>
      <div className={styles.panelBackdrop} onClick={onClose} />
      <div className={styles.detailPanel}>
        <div className={styles.panelHeader}>
          <span className={styles.panelTitle}>{filter.label}</span>
          <button className={styles.panelClose} onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {isLoading ? (
          <div className={styles.panelBody}>
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className={styles.panelProviderSkeleton}>
                <Skeleton width="60%" height="14px" />
                <Skeleton width="40%" height="12px" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className={styles.panelCount}>
              {data?.count ?? 0} provider{data?.count !== 1 ? 's' : ''}
            </div>
            <div className={styles.panelBody}>
              {data?.results.length === 0 && (
                <div className={styles.panelEmpty}>No providers found.</div>
              )}
              {data?.results.map((p) => (
                <div
                  key={p.id}
                  className={styles.panelProvider}
                  onClick={() => onProviderClick(p.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') onProviderClick(p.id);
                  }}
                >
                  <div className={styles.panelProviderTop}>
                    <span className={styles.panelProviderName}>{p.nombre_comercial}</span>
                    <StarRating value={p.calificacion} />
                  </div>
                  <div className={styles.panelProviderMeta}>
                    {p.ciudad && (
                      <span className={styles.panelMeta}>
                        <MapPin size={10} />
                        {p.ciudad}
                      </span>
                    )}
                    {p.email && (
                      <span className={styles.panelMeta}>
                        <Mail size={10} />
                        {p.email}
                      </span>
                    )}
                    {p.telefono && (
                      <span className={styles.panelMeta}>
                        <Phone size={10} />
                        {p.telefono}
                      </span>
                    )}
                    {p.created_by_email && (
                      <span className={styles.panelMeta}>
                        <Mail size={10} />
                        {p.created_by_email}
                      </span>
                    )}
                  </div>
                  {p.services?.length > 0 && (
                    <div className={styles.panelTags}>
                      {p.services.slice(0, 4).map((s) => (
                        <span key={s.id} className={styles.panelTag}>
                          {s.service_details?.name ?? 'Unknown'}
                        </span>
                      ))}
                      {p.services.length > 4 && (
                        <span className={styles.panelTagMore}>+{p.services.length - 4}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// --- Main component ---

export function ProviderStats() {
  const navigate = useNavigate();
  const [countryFilter, setCountryFilter] = useState<CountryFilter>('All');
  const [activePanel, setActivePanel] = useState<PanelFilter | null>(null);
  const [creatorId, setCreatorId] = useState('');

  const { data: users } = useUsers();
  const creatorOptions = [
    { value: '', label: 'All Creators' },
    ...(users ?? []).map((u) => ({
      value: String(u.id),
      label: `${u.first_name} ${u.last_name}`.trim() || u.email,
    })),
  ];

  const {
    data: stats,
    isLoading,
    isError,
  } = useQuery<DashboardStats>({
    queryKey: ['provider-dashboard-stats', creatorId],
    queryFn: () => {
      const params: Record<string, string> = {};
      if (creatorId) params.created_by = creatorId;
      return api.get(API_ROUTES.providerDashboardStats, { params }).then((r) => r.data);
    },
  });

  useEffect(() => {
    if (isError) {
      toast.error('Error loading provider stats. Please try again.');
    }
  }, [isError]);

  const filtered = useMemo(() => {
    if (!stats) return null;
    if (countryFilter === 'All') return stats;

    const { by_city, by_country_service, overview, state_distribution } = stats;
    const filteredCities = by_city.filter((c) => cityToCountry(c.city) === countryFilter);
    const filteredCountryService = by_country_service.filter((cs) => cs.country === countryFilter);
    const countryServices = filteredCountryService.flatMap((cs) => cs.services);
    const serviceMap = new Map<string, number>();
    for (const s of countryServices) {
      serviceMap.set(s.service, (serviceMap.get(s.service) || 0) + s.count);
    }
    const filteredByService = Array.from(serviceMap.entries())
      .map(([service, count]) => ({ service, slug: '', count }))
      .sort((a, b) => b.count - a.count);
    const countryData = by_country_service.find((cs) => cs.country === countryFilter);
    const totalFiltered =
      countryData?.provider_count ?? filteredCities.reduce((sum, c) => sum + c.count, 0);

    return {
      overview: { ...overview, total: totalFiltered || overview.total },
      by_city: filteredCities,
      by_service: filteredByService.length > 0 ? filteredByService : stats.by_service,
      by_country_service: filteredCountryService,
      state_distribution,
      rating_distribution: stats.rating_distribution,
    } as DashboardStats;
  }, [stats, countryFilter]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.topRow}>
          <div className={styles.filterPills}>
            {COUNTRY_OPTIONS.map((o) => (
              <span key={o} className={styles.pill}>
                <Skeleton width="48px" height="14px" />
              </span>
            ))}
          </div>
          <div className={styles.statCards}>
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className={styles.statCard}>
                <Skeleton width="32px" height="20px" />
                <Skeleton width="50px" height="10px" />
              </div>
            ))}
          </div>
        </div>
        <div className={styles.mainGrid}>
          {['By City', 'By Service', 'Status', 'By Country', 'Contact', 'Documents'].map(
            (title, i) => (
              <div key={i} className={styles.panel}>
                <div className={styles.panelInnerHeader}>
                  <span className={styles.panelHeading}>{title}</span>
                </div>
                <div className={i < 2 ? styles.scrollList : styles.stackedList}>
                  {Array.from({ length: i < 2 ? 5 : 3 }, (_, j) => (
                    <div key={j} className={styles.barRow}>
                      <Skeleton width="80px" height="12px" />
                      <div className={styles.barTrack}>
                        <Skeleton width={`${60 - j * 8}%`} height="100%" />
                      </div>
                      <Skeleton width="20px" height="12px" />
                    </div>
                  ))}
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    );
  }

  if (!filtered) return null;

  const { overview, by_city, by_service, state_distribution } = filtered;

  const maxCity = Math.max(...by_city.map((c) => c.count), 1);
  const maxService = Math.max(...by_service.map((s) => s.count), 1);
  const maxState = Math.max(...state_distribution.map((s) => s.count), 1);

  // Country totals for bottom-left panel
  const countryTotals = (stats?.by_country_service ?? [])
    .map((cs) => ({ country: cs.country, count: cs.provider_count }))
    .sort((a, b) => b.count - a.count);
  const maxCountry = Math.max(...countryTotals.map((c) => c.count), 1);

  // Contact coverage calculations
  const emailOnly = overview.with_email - overview.with_both_contacts;
  const phoneOnly = overview.with_phone - overview.with_both_contacts;
  const both = overview.with_both_contacts;
  const neither =
    overview.total - overview.with_email - overview.with_phone + overview.with_both_contacts;
  const contactItems = [
    { label: 'Both contacts', count: both, color: 'var(--color-success)' },
    { label: 'Email only', count: emailOnly, color: 'var(--color-info)' },
    { label: 'Phone only', count: phoneOnly, color: 'var(--color-warning)' },
    { label: 'Neither', count: neither, color: 'var(--color-text-muted)' },
  ];
  const maxContact = Math.max(...contactItems.map((c) => c.count), 1);

  // Document coverage
  const docItems = Object.entries(overview.document_distribution).map(([key, count]) => ({
    label:
      key === '0' ? 'No docs' : key === '3+' ? '3+ docs' : `${key} doc${key === '1' ? '' : 's'}`,
    count,
    color:
      key === '0'
        ? 'var(--color-text-muted)'
        : key === '1'
          ? 'var(--color-info)'
          : key === '2'
            ? 'var(--color-primary)'
            : 'var(--color-success)',
  }));
  const maxDoc = Math.max(...docItems.map((d) => d.count), 1);

  const STATE_CONFIG: Record<string, { label: string; color: string }> = {
    activo: { label: 'Active', color: 'var(--color-success)' },
    inactivo: { label: 'Inactive', color: 'var(--color-text-muted)' },
    borrador: { label: 'Draft', color: 'var(--color-warning)' },
  };

  return (
    <div className={styles.container}>
      {/* Top row: filter pills + stat cards */}
      <div className={styles.topRow}>
        <div className={styles.filterPills}>
          <Globe size={14} className={styles.globeIcon} />
          {COUNTRY_OPTIONS.map((option) => (
            <button
              key={option}
              className={`${styles.pill} ${countryFilter === option ? styles.pillActive : ''}`}
              onClick={() => setCountryFilter(option)}
            >
              {option === 'All' ? 'All' : option}
              {option !== 'All' && stats?.by_country_service && (
                <span className={styles.pillBadge}>
                  {stats.by_country_service.find((c) => c.country === option)?.provider_count ?? 0}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className={styles.creatorFilter}>
          <Select
            value={creatorId}
            onChange={(e) => setCreatorId(e.target.value)}
            options={creatorOptions}
            size="sm"
            searchable
          />
        </div>

        <div className={styles.statCards}>
          <button
            className={styles.statCard}
            onClick={() => setActivePanel({ type: 'status', label: 'All Providers', state: '' })}
          >
            <span className={styles.statNum}>{overview.total}</span>
            <span className={styles.statLabel}>Total</span>
          </button>
          <button
            className={styles.statCard}
            onClick={() =>
              setActivePanel({ type: 'status', label: 'Active Providers', state: 'activo' })
            }
          >
            <span className={`${styles.statNum} ${styles.statNumGreen}`}>{overview.active}</span>
            <span className={styles.statLabel}>Active</span>
          </button>
          <button
            className={styles.statCard}
            onClick={() =>
              setActivePanel({ type: 'status', label: 'Providers with Email', state: 'activo' })
            }
          >
            <span className={`${styles.statNum} ${styles.statNumBlue}`}>{overview.with_email}</span>
            <span className={styles.statLabel}>With Email</span>
          </button>
          <button
            className={styles.statCard}
            onClick={() =>
              setActivePanel({ type: 'status', label: 'Providers with Phone', state: 'activo' })
            }
          >
            <span className={`${styles.statNum} ${styles.statNumAmber}`}>
              {overview.with_phone}
            </span>
            <span className={styles.statLabel}>With Phone</span>
          </button>
        </div>
      </div>

      {/* Main 2×3 grid */}
      <div className={styles.mainGrid}>
        {/* Row 1, Col 1: By City */}
        <div className={styles.panel}>
          <div className={styles.panelInnerHeader}>
            <Building2 size={13} className={styles.panelIcon} />
            <span className={styles.panelHeading}>By City</span>
            <span className={styles.panelHint}>click to drill down</span>
          </div>
          <div className={styles.scrollList}>
            {by_city.slice(0, 15).map((c) => (
              <BarRow
                key={c.city}
                label={c.city}
                count={c.count}
                maxCount={maxCity}
                color={COUNTRY_COLORS[cityToCountry(c.city)] ?? '#94a3b8'}
                onClick={() =>
                  setActivePanel({
                    type: 'city',
                    label: `Providers in ${c.city}`,
                    city: c.city,
                  })
                }
              />
            ))}
            {by_city.length === 0 && <div className={styles.emptyMsg}>No city data</div>}
          </div>
        </div>

        {/* Row 1, Col 2: By Service */}
        <div className={styles.panel}>
          <div className={styles.panelInnerHeader}>
            <MapPin size={13} className={styles.panelIcon} />
            <span className={styles.panelHeading}>By Service</span>
            <span className={styles.panelHint}>click to drill down</span>
          </div>
          <div className={styles.scrollList}>
            {by_service.map((s) => (
              <BarRow
                key={s.service}
                label={s.service}
                count={s.count}
                maxCount={maxService}
                color="var(--color-primary)"
                onClick={() =>
                  setActivePanel({
                    type: 'service',
                    label: `Providers: ${s.service}`,
                    slug: s.slug,
                  })
                }
              />
            ))}
            {by_service.length === 0 && <div className={styles.emptyMsg}>No service data</div>}
          </div>
        </div>

        {/* Row 1, Col 3: Status */}
        <div className={styles.panel}>
          <div className={styles.panelInnerHeader}>
            <span className={styles.panelHeading}>Status</span>
          </div>
          <div className={styles.stackedList}>
            {state_distribution.map((s) => {
              const cfg = STATE_CONFIG[s.state] ?? { label: s.state, color: '#94a3b8' };
              return (
                <BarRow
                  key={s.state}
                  label={cfg.label}
                  count={s.count}
                  maxCount={maxState}
                  color={cfg.color}
                  onClick={() =>
                    setActivePanel({
                      type: 'status',
                      label: `${cfg.label} Providers`,
                      state: s.state,
                    })
                  }
                />
              );
            })}
          </div>
        </div>

        {/* Row 2, Col 1: By Country */}
        <div className={styles.panel}>
          <div className={styles.panelInnerHeader}>
            <Globe size={13} className={styles.panelIcon} />
            <span className={styles.panelHeading}>By Country</span>
          </div>
          <div className={styles.stackedList}>
            {countryTotals.map((c) => (
              <BarRow
                key={c.country}
                label={c.country}
                count={c.count}
                maxCount={maxCountry}
                color={COUNTRY_COLORS[c.country] ?? '#94a3b8'}
                onClick={() => {
                  setCountryFilter(c.country as CountryFilter);
                }}
              />
            ))}
            {countryTotals.length === 0 && <div className={styles.emptyMsg}>No country data</div>}
          </div>
        </div>

        {/* Row 2, Col 2: Contact Coverage */}
        <div className={styles.panel}>
          <div className={styles.panelInnerHeader}>
            <Mail size={13} className={styles.panelIcon} />
            <span className={styles.panelHeading}>Contact Coverage</span>
          </div>
          <div className={styles.stackedList}>
            {contactItems.map((item) => (
              <BarRow
                key={item.label}
                label={item.label}
                count={item.count}
                maxCount={maxContact}
                color={item.color}
                onClick={() =>
                  setActivePanel({
                    type: 'status',
                    label: `Providers: ${item.label}`,
                    state: 'activo',
                  })
                }
              />
            ))}
          </div>
        </div>

        {/* Row 2, Col 3: Document Coverage */}
        <div className={styles.panel}>
          <div className={styles.panelInnerHeader}>
            <FileText size={13} className={styles.panelIcon} />
            <span className={styles.panelHeading}>Documents</span>
          </div>
          <div className={styles.stackedList}>
            {docItems.map((item) => (
              <BarRow
                key={item.label}
                label={item.label}
                count={item.count}
                maxCount={maxDoc}
                color={item.color}
                onClick={() =>
                  setActivePanel({
                    type: 'status',
                    label: `Providers: ${item.label}`,
                    state: '',
                  })
                }
              />
            ))}
          </div>
        </div>
      </div>

      {/* Slide-over detail panel */}
      {activePanel && (
        <DetailPanel
          filter={activePanel}
          onClose={() => setActivePanel(null)}
          creatorId={creatorId}
          onProviderClick={(id) =>
            navigate(ROUTES.dashboardProviderDetails.replace(':id', id))
          }
        />
      )}
    </div>
  );
}
